"use client";

import { useState } from "react";
import { SITE_CONFIG } from "../../lib/site-config";

type TabType = "credit" | "debit" | "corporate";

export default function EMITermsClient() {
  const [activeTab, setActiveTab] = useState<TabType>("credit");

  return (
    <main className="max-w-5xl mx-auto px-4 py-14 animate-in fade-in duration-700">
      {/* ================= HEADER ================= */}
      <div className="mb-14 text-center">
        <h1 className="text-4xl font-semibold tracking-tight">Payment & EMI Policy</h1>
        <p className="text-sm text-gray-500 mt-3">
          Explore flexible payment options tailored for individuals and businesses
        </p>
      </div>

      {/* ================= CATEGORY SELECTOR (Blue Pills) ================= */}
      <div className="flex flex-wrap justify-center gap-3 mb-12">
        <TabButton 
          active={activeTab === "credit"} 
          onClick={() => setActiveTab("credit")}
          label="Credit Card EMI" 
        />
        <TabButton 
          active={activeTab === "debit"} 
          onClick={() => setActiveTab("debit")}
          label="Debit Card EMI" 
        />
        <TabButton 
          active={activeTab === "corporate"} 
          onClick={() => setActiveTab("corporate")}
          label="Corporate Solutions" 
        />
      </div>

      {/* ================= DYNAMIC CONTENT ================= */}
      <div key={activeTab} className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
        
        {activeTab === "credit" && (
          <>
            <section className="bg-white border rounded-2xl p-8 text-sm text-gray-700 leading-relaxed shadow-sm">
              <p>
                Our <strong>Standard Credit Card EMI</strong> allows you to convert high-value purchases into manageable 
                installments. We partner with 18+ major banks to offer 0% interest (ZPP) on selected tenures.
              </p>
            </section>

            <InfoCard title="Credit EMI Details">
              <ul className="list-disc list-inside space-y-2">
                <li>Available for tenures of 3, 6, 9, 12, 18, and 24 months.</li>
                <li>Minimum purchase value: 5,000 BDT.</li>
                <li>Instant approval at the time of checkout.</li>
                <li>No documentation required; only a valid credit card with sufficient limit.</li>
              </ul>
            </InfoCard>

            <InfoCard title="Partner Banks">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {["City Bank", "EBL", "SCB", "HSBC", "Brac Bank", "DBBL", "MTB", "UBL"].map(bank => (
                  <div key={bank} className="p-3 border rounded-xl text-center text-xs font-medium bg-gray-50 uppercase tracking-wide">
                    {bank}
                  </div>
                ))}
              </div>
            </InfoCard>
          </>
        )}

        {activeTab === "debit" && (
          <>
            <section className="bg-white border rounded-2xl p-8 text-sm text-gray-700 leading-relaxed shadow-sm">
              <p>
                <strong>Debit Card EMI</strong> is a pre-approved loan facility for eligible customers. 
                Unlike credit cards, this depends on your banking history and average balance.
              </p>
            </section>

            <InfoCard title="How it Works">
              <ol className="list-decimal list-inside space-y-3">
                <li>Check your eligibility via your bank&apos;s mobile app or SMS.</li>
                <li>Select &quot;Debit Card EMI&quot; during checkout.</li>
                <li>The first installment is usually deducted instantly.</li>
                <li>Ensure your linked account has sufficient funds on the 1st of every month.</li>
              </ol>
            </InfoCard>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoCard title="Requirements">
                <ul className="list-disc list-inside space-y-1">
                  <li>Active savings/current account</li>
                  <li>Linked mobile number</li>
                  <li>Consistent transaction history</li>
                  <li>Age: 21 - 60 years</li>
                </ul>
              </InfoCard>
              <InfoCard title="Eligible Banks">
                <p className="text-gray-500 italic mb-2">Currently supported for:</p>
                <div className="flex gap-2">
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-md border border-blue-100 font-bold text-xs uppercase">City Bank</span>
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-md border border-blue-100 font-bold text-xs uppercase">Brac Bank</span>
                </div>
              </InfoCard>
            </div>
          </>
        )}

        {activeTab === "corporate" && (
          <>
            <section className="bg-blue-600 rounded-2xl p-8 text-white shadow-lg">
              <h2 className="text-xl font-bold mb-2">Corporate & Business Procurement</h2>
              <p className="text-blue-100 text-sm">
                Customized payment cycles and bulk purchase benefits for registered companies and enterprises.
              </p>
            </section>

            <InfoCard title="Corporate Card Benefits">
              <ul className="list-disc list-inside space-y-2">
                <li>Extended credit lines for Tax registered businesses.</li>
                <li>Consolidated billing for multiple employee purchases.</li>
                <li>Dedicated account manager for bulk orders.</li>
                <li>Special pricing on enterprise-grade hardware (ThinkPads, Latitudes, etc).</li>
              </ul>
            </InfoCard>

            <InfoCard title="Documentation Needed">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 border rounded-xl bg-gray-50">
                  <p className="font-semibold text-gray-900 mb-1">Small Businesses</p>
                  <p className="text-xs">Trade License, Owner NID, 6-month bank statement.</p>
                </div>
                <div className="p-4 border rounded-xl bg-gray-50">
                  <p className="font-semibold text-gray-900 mb-1">Limited Companies</p>
                  <p className="text-xs">Incorporation Certificate, Board Resolution, Form XII.</p>
                </div>
              </div>
            </InfoCard>
          </>
        )}

        {/* ================= SHARED FOOTER ================= */}
        <section className="border rounded-2xl p-8 bg-gray-50">
          <h2 className="text-lg font-semibold mb-3">General Support</h2>
          <p className="text-sm text-gray-600">
            All EMI transactions are subject to <strong>{SITE_CONFIG.name}</strong>&apos;s verification. 
            Final interest rates are determined by the respective banks.
          </p>
          <div className="mt-6 flex flex-wrap gap-6 text-sm">
            <div>
              <p className="text-gray-400 text-xs font-bold uppercase mb-1">Call for Assistance</p>
              <p className="font-medium">{SITE_CONFIG.phone}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs font-bold uppercase mb-1">Corporate Email</p>
              <p className="font-medium">{SITE_CONFIG.email}</p>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

/* ================= HELPERS ================= */

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`
        px-6 py-2 rounded-full text-xs font-semibold transition-all duration-300 flex items-center gap-2
        ${active 
          ? "bg-blue-600 text-white shadow-md scale-105" 
          : "bg-white border border-gray-200 text-gray-500 hover:border-blue-300 hover:text-blue-600"}
      `}
    >
      {active && (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      )}
      {label}
    </button>
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white border rounded-2xl p-8 space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}